// ============================================
// FILE: src/components/CategoryShelves.jsx
// ============================================
//
// The home page's "All" view: products grouped into one shelf per category,
// each with the category's own banners above its products. Replaces the single
// undifferentiated "Our Products" grid (a selected category or a search still
// gets the flat grid).
//
// Both home screens use it; each passes its own product card so the shelves
// match the rest of that layout.

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import apiService from '../apiService';
import CategoryBannerCarousel from './CategoryBannerCarousel';
import { buildCategoryShelves } from '../utils/categoryShelves';

const CategoryShelves = ({
  categories = [],
  products = [],
  onCategoryClick,
  setSelectedCategory,
  renderProductCard,
  isDesktop = false,
  // Enough to browse; "See all" opens the category for the rest.
  maxProducts = 10,
  // Rendered instead when no category has products (e.g. category names that
  // don't match the product data), so the page never ends up empty.
  fallback = null,
}) => {
  const [banners, setBanners] = useState([]);

  // One request for every shelf: without a category the endpoint returns all
  // active banners, and they are sorted into shelves locally.
  useEffect(() => {
    let cancelled = false;
    apiService.getBanners({ position: 'all' })
      .then((resp) => {
        if (cancelled) return;
        const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
        setBanners(rows);
      })
      // A failed banner fetch leaves shelves without banners, never breaks them.
      .catch(() => { if (!cancelled) setBanners([]); });
    return () => { cancelled = true; };
  }, []);

  const shelves = useMemo(
    () => buildCategoryShelves(categories, products, banners),
    [categories, products, banners]
  );

  const openCategory = (category) => {
    if (onCategoryClick) onCategoryClick(category);
    else if (setSelectedCategory) setSelectedCategory(category.name);
  };

  if (shelves.length === 0) return fallback;

  return (
    <div className="products-section">
      {shelves.map(({ category, products: shelfProducts, banners: shelfBanners }) => {
        const shown = shelfProducts.slice(0, maxProducts);
        return (
          <section
            key={category.id}
            className={isDesktop ? 'mb-12' : 'mb-6 bg-white py-4'}
            aria-label={category.name}
          >
            <div className={`flex items-center justify-between gap-3 ${isDesktop ? 'mb-4' : 'px-4 mb-3'}`}>
              <div className="min-w-0">
                <h2 className={`font-bold text-[#282c3f] font-display truncate ${isDesktop ? 'text-2xl' : 'text-base'}`}>
                  {category.name}
                </h2>
                <p className={`text-[#94969f] ${isDesktop ? 'text-sm' : 'text-xs'}`}>
                  {shelfProducts.length} {shelfProducts.length === 1 ? 'product' : 'products'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => openCategory(category)}
                className={`flex items-center gap-0.5 flex-shrink-0 font-semibold text-[#ff3f6c] active:scale-95 transition-transform ${isDesktop ? 'text-sm' : 'text-xs'}`}
              >
                See all <ChevronRight size={isDesktop ? 16 : 14} />
              </button>
            </div>

            {shelfBanners.length > 0 && (
              <CategoryBannerCarousel
                banners={shelfBanners}
                categories={categories}
                onCategoryClick={onCategoryClick}
                setSelectedCategory={setSelectedCategory}
                isDesktop={isDesktop}
                compact
              />
            )}

            {isDesktop ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {shown.map((product, index) => renderProductCard(product, index))}
              </div>
            ) : (
              <div className="flex gap-3 overflow-x-auto px-4 pb-1 hide-scrollbar">
                {shown.map((product, index) => (
                  <div key={product.id} className="flex-shrink-0" style={{ width: 168 }}>
                    {renderProductCard(product, index)}
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default CategoryShelves;
